import { useMemo, useRef, useState } from 'react';
import {
  buildEntityIndex,
  listEntities,
  normalizeKindId,
  resolveRef,
  resolveKind,
  type Adventure,
  type IndexedEntity,
} from '@gm-binder/shared';
import styles from './RefPicker.module.css';

export interface RefPickerProps {
  /** The adventure whose entities can be referenced. */
  adventure: Adventure;
  /** Current reference value (`"kind:id"`), or null/undefined when unset. */
  value?: string | null;
  /** Called with the new reference, or null when cleared. */
  onChange: (ref: string | null) => void;
  /** Restrict choices to one or more kinds; omit to allow any entity. */
  target?: string | string[];
  placeholder?: string;
  /** Show a clear ("×") control when a value is set. Defaults to true. */
  allowClear?: boolean;
  id?: string;
}

/**
 * Searchable entity picker that emits a namespaced `"kind:id"` reference. Decoupled from the
 * binder runtime (operates on the passed `adventure`), so it works inside the editor too.
 */
export function RefPicker({
  adventure,
  value,
  onChange,
  target,
  placeholder = 'Cerca un\'entità…',
  allowClear = true,
  id,
}: RefPickerProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | undefined>(undefined);

  const targets = useMemo(
    () => (target ? (Array.isArray(target) ? target : [target]).map(normalizeKindId) : null),
    [target],
  );

  const index = useMemo(() => buildEntityIndex(adventure), [adventure]);
  // Resolve with the target kind as a hint so bare ids (e.g. "cucina") resolve, not just
  // fully-namespaced "kind:id" refs — mirrors how the read-only preview resolves references.
  const selected = value ? resolveRef(index, value, targets?.[0]) : undefined;

  const options = useMemo<IndexedEntity[]>(() => {
    const term = query.trim().toLowerCase();
    return listEntities(adventure)
      .filter((e) => (targets ? targets.includes(e.kind) : true))
      .filter((e) => (term ? e.label.toLowerCase().includes(term) : true))
      .slice(0, 50);
  }, [adventure, targets, query]);

  const kindLabel = (kind: string) =>
    resolveKind(kind, adventure.kinds as Parameters<typeof resolveKind>[1])?.label ?? kind;

  const pick = (entity: IndexedEntity) => {
    onChange(entity.ref);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className={styles.root}>
      {selected ? (
        <div className={styles.selected}>
          <span className={styles.selectedIcon} aria-hidden>
            {selected.icon ?? '🔗'}
          </span>
          <span className={styles.selectedLabel}>{selected.label}</span>
          <span className={styles.selectedKind}>{kindLabel(selected.kind)}</span>
          {allowClear && (
            <button
              type="button"
              className={styles.clear}
              aria-label="Rimuovi riferimento"
              onClick={() => onChange(null)}
            >
              ×
            </button>
          )}
        </div>
      ) : value ? (
        <div className={styles.selected}>
          <span className={styles.danglingLabel}>⚠ {value} (non trovato)</span>
          {allowClear && (
            <button
              type="button"
              className={styles.clear}
              aria-label="Rimuovi riferimento"
              onClick={() => onChange(null)}
            >
              ×
            </button>
          )}
        </div>
      ) : (
        <input
          id={id}
          className={styles.input}
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            blurTimer.current = window.setTimeout(() => setOpen(false), 120);
          }}
        />
      )}

      {open && !selected && (
        <div
          className={styles.dropdown}
          onMouseDown={() => {
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
          }}
        >
          {options.length === 0 ? (
            <div className={styles.muted}>Nessuna entità</div>
          ) : (
            options.map((e) => (
              <button
                type="button"
                key={e.ref}
                className={styles.option}
                onClick={() => pick(e)}
              >
                <span className={styles.optionIcon} aria-hidden>
                  {e.icon ?? '🔗'}
                </span>
                <span className={styles.optionLabel}>{e.label}</span>
                <span className={styles.optionKind}>{kindLabel(e.kind)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
